/**
 * `<AnalysisPasteBox>` — primary surface on the Interview Analysis tab
 * for capturing a recruiter's post-interview observations.
 *
 * Phase 2.6 — interview-prep-spec.md §7.3.
 *
 * Layout:
 *   - Top instruction strip explaining what to paste (transcript /
 *     bullets / paragraph; AI handles the rest).
 *   - Multi-line textarea with no character limit; placeholder shows
 *     a realistic example.
 *   - Bottom action bar with live char count (informational) +
 *     [Save & analyze] primary CTA + [Skip this stage] tertiary.
 *
 * The Save click is the cheap path (no credit charge) — the AI parse
 * runs separately via Regenerate. Skip is wired to the parent so the
 * "are you sure?" modal can intercept.
 *
 * Decision 2 PERMANENT CANON: this paste-box is the sole chain-
 * enrichment input surface. NO per-question recruiter composer
 * lives on the Prep tab.
 */
import { useState, type JSX, type ChangeEvent } from "react";
import { Button } from "@/components/common/Button";

interface AnalysisPasteBoxProps {
  stageName: string;
  candidateName: string;
  initialValue?: string;
  isPending: boolean;
  onSave: (transcript: string) => void;
  onSkip: () => void;
}

export function AnalysisPasteBox({
  stageName,
  candidateName,
  initialValue,
  isPending,
  onSave,
  onSkip,
}: AnalysisPasteBoxProps): JSX.Element {
  const [value, setValue] = useState<string>(initialValue ?? "");
  const trimmed = value.trim();
  const canSave = trimmed.length > 0 && !isPending;

  const firstName = candidateName.split(" ")[0] ?? candidateName;

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    setValue(e.target.value);
  };

  return (
    <div
      className="flex flex-col gap-4 px-content-px py-5"
      data-testid="analysis-paste-box"
    >
      <header className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <span className="text-xs uppercase tracking-wider text-text-tertiary">
            Analyzing
          </span>
          <h2 className="text-2xl font-semibold text-text-emphasis">
            {stageName}
          </h2>
        </div>
        <Button
          variant="tertiary"
          onClick={onSkip}
          disabled={isPending}
          data-testid="analysis-skip"
          className="text-sm"
        >
          Skip this stage
        </Button>
      </header>

      <div className="overflow-hidden rounded-lg border border-border-default bg-surface-card">
        <div className="border-b border-border-default bg-surface-subtle px-4 py-2 text-xs text-text-secondary">
          Paste anything — interview transcript, recruiter notes, bullet
          points, a one-paragraph summary. CertAIn will pull out what it
          needs.
        </div>
        <textarea
          value={value}
          onChange={handleChange}
          placeholder={`E.g. '${firstName} was strong on system design but lukewarm on the team-fit questions. Recommended next round: deeper-dive on architectural trade-offs…'`}
          className="min-h-[280px] w-full resize-y bg-transparent p-4 text-sm leading-relaxed text-text-primary placeholder:text-text-tertiary focus:outline-none"
          aria-label={`Interview observations for ${stageName}`}
          data-testid="analysis-paste-textarea"
          disabled={isPending}
        />
        <div className="flex items-center justify-between border-t border-border-default p-4">
          <span
            className="text-xs text-text-tertiary"
            data-testid="analysis-paste-charcount"
          >
            {value.length.toLocaleString()} characters
          </span>
          <Button
            variant="primary"
            onClick={() => onSave(trimmed)}
            disabled={!canSave}
            loading={isPending}
            data-testid="analysis-paste-save"
          >
            Save & analyze
          </Button>
        </div>
      </div>
    </div>
  );
}
